import { SelectionModel } from "@angular/cdk/collections";
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { Router } from "@angular/router";
import { AlertDialogComponent } from "src/app/alert-dialog/alert-dialog.component";
import { TableComponent } from "src/app/table.component";
import { CareplusNewCustomer } from "src/models/careplus-new-customer";
import { CareplusAgencyService } from "src/services/careplus-agency-service";
import { LinkSentSuccessDialogComponent } from "../link-sent-success-dialog/link-sent-success-dialog.component";
import { UploadEmployeesDialogComponent } from "../upload-employees-dialog/upload-employees-dialog.component";
import { ReportService } from "src/services/report.service";

@Component({
  selector: "app-careplus-imported-employees",
  templateUrl: "./careplus-imported-employees.component.html",
  styleUrls: ["./careplus-imported-employees.component.css"],
})
export class CareplusImportedEmployeesComponent extends TableComponent<CareplusNewCustomer> {
  private _careplusAgencyService: CareplusAgencyService;
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _router = inject(Router);
  private _reportService = inject(ReportService);
  selection = new SelectionModel<any>(true, []);
  @Output() dataLoaded = new EventEmitter<any[]>();
  @Output() onUploadClick = new EventEmitter<void>();
  @ViewChild("input") input!: ElementRef<HTMLInputElement>;
  sendingInvite: boolean = false;
  search: string = "";
  isOpen = false;

  constructor(careplusAgencyService: CareplusAgencyService) {
    super(careplusAgencyService);
    this._careplusAgencyService = careplusAgencyService;
    this.dataSubject.next([]);
    this.dataSubject.subscribe((data) => {
      this.dataLoaded.emit(data);
    });
  }

  displayedColumns: string[] = [
    "select",
    "id",
    "status",
    "firstName",
    "lastName",
    "employeeId",
    "gender",
    "dateOfBirth",
    "email",
    "phoneNumber",
    "importedAt",
    "actions",
  ];

  ngOnInit(): void {
    super.ngOnInit();
  }
  filter() {
    this.getCount().subscribe((res) => {
      this.count = res.count;
      this.loadRequests();
    });
  }

  override getTableData() {
    return super.getTableData();
  }

  override getCount() {
    return this._careplusAgencyService.getCount({
      filter: this.search,
    });
  }

  protected override getData(): any {
    let pageSize = this.paginator?.pageSize ? this.paginator.pageSize : 10;
    return {
      skip: (this.paginator?.pageIndex ?? 0) * pageSize,
      take: pageSize,
      filter: this.search,
      orderBy: this.sort?.active,
      orderByDescending: this.sort?.direction === "desc",
    };
  }

  detectLanguage(ownerName: string): string {
    if (!ownerName) return "unknown";

    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
    if (arabicRegex.test(ownerName)) {
      return "ar";
    }
    const englishRegex = /^[A-Za-z0-9\s]+$/;
    if (englishRegex.test(ownerName)) {
      return "en";
    }
    return "unknown";
  }

  sendLinkToSelected() {
    const selectedCustomers = this.selection?.selected ?? [];

    if (selectedCustomers.length === 0) {
      return;
    }

    const activeCustomers = selectedCustomers;

    const formattedReqPayload = activeCustomers
      .map((customer) => {
        const name =
          `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim();
        const detectedLang = this.detectLanguage(name);
        return { id: customer?.id, lang: detectedLang };
      })
      .filter((item) => item !== null);

    if (formattedReqPayload?.length === 0) {
      return;
    }

    this.sendingInvite = true;
    this._careplusAgencyService.sendCareplusLink(formattedReqPayload).subscribe(
      (res) => {
        this.sendingInvite = false;
        this._dialog.open(LinkSentSuccessDialogComponent, {
          width: "500px",
          panelClass: "custom-dialog-container",
        });
        this.selection.clear();
      },
      (err) => {
        this.sendingInvite = false;
        this._snackBar.open(err, "CANCEL", {
          horizontalPosition: "right",
          duration: 4000,
        });
      },
    );
  }

  sendCareplusLink() {
    const dialogRef = this._dialog.open(AlertDialogComponent, {
      data: {
        title: "Send Care+ Invite Link",
        innerHTML:
          "Do you want to send the invite link to all employees <strong>who have not submitted</strong> their details for Care+?",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "true") {
        this.sendingInvite = true;
        const requestPayload = {
          skip: 0,
          take: 999999,
          filter: this.input?.nativeElement?.value ?? "",
          orderBy: this.sort?.active,
          orderByDescending: this.sort?.direction === "desc",
        };

        this._careplusAgencyService
          .getData(requestPayload)
          .subscribe((allCustomers: CareplusNewCustomer[]) => {
            if (!allCustomers || allCustomers.length === 0) {
              this.sendingInvite = false;
              return;
            }

            const activeCustomers = allCustomers;

            const formattedReqPayload = activeCustomers
              .map((customer) => {
                const name =
                  `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim();
                const detectedLang = this.detectLanguage(name);
                return { id: customer?.id, lang: detectedLang };
              })
              .filter((item) => item !== null);

            if (formattedReqPayload?.length === 0) {
              this.sendingInvite = false;
              return;
            }

            this._careplusAgencyService
              .sendCareplusLink(formattedReqPayload)
              .subscribe(
                (res) => {
                  this.sendingInvite = false;
                  this._dialog.open(LinkSentSuccessDialogComponent, {
                    width: "372px",
                    panelClass: "custom-dialog-container",
                  });
                },
                (err) => {
                  this.sendingInvite = false;
                  this._snackBar.open(err, "CLOSE", {
                    horizontalPosition: "right",
                    duration: 4000,
                  });
                },
              );
          });
      }
    });
  }

  isSelectable(row: CareplusNewCustomer): boolean {
    return row.status === "IMPORTED" || row.status === "MESSAGE_SENT";
  }

  override isAllSelected() {
    const data = this.dataSubject.getValue();
    const selectableRows = data.filter((row) => this.isSelectable(row));
    const numSelected = this.selection.selected.length;
    return numSelected === selectableRows.length && selectableRows.length > 0;
  }

  override masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      const data = this.dataSubject.getValue();
      this.selection.clear();
      data.forEach((row) => {
        if (this.isSelectable(row)) {
          this.selection.select(row);
        }
      });
    }
  }

  toggleActive(employee: CareplusNewCustomer, makeActive: boolean) {
    this._careplusAgencyService
      .activateDeactivate(employee.id, makeActive)
      .subscribe({
        next: () => {
          employee.active = makeActive;
        },
        error: (err) => {
          employee.active = !makeActive;
          this._snackBar.open(
            err ||
              `Failed to ${makeActive ? "activate" : "deactivate"} employee.`,
            "CLOSE",
            { horizontalPosition: "right", duration: 4000 },
          );
        },
      });
  }

  openCustomer(row) {
    const status = row?.status?.toUpperCase();

    if (status === "FORM_SUBMITED") {
      this._router.navigate(["/careplus/careplus-add-customers/" + row.id]);
    }
  }

  deleteEmployee(row) {
    this._careplusAgencyService.deleteImportedEmployee(row.id).subscribe({
      next: () => {
        const currentData = this.dataSubject.value;

        const updatedData = currentData.filter((emp) => emp.id !== row.id);

        this.dataSubject.next(updatedData);

        this.count = this.count - 1;

        this.selection.deselect(row);

        this._snackBar.open("Employee deleted successfully", "Close", {
          duration: 2000,
        });
      },
      error: (error) => {
        this._snackBar.open(
          "Failed to delete employee. Please try again.",
          "Close",
          {
            duration: 3000,
          },
        );
      },
    });
  }

  openUploadDialog() {
    const dialogRef = this._dialog.open(UploadEmployeesDialogComponent, {
      width: "660px",
      panelClass: "custom-dialog",
    });
    dialogRef.afterClosed().subscribe((uploaded: boolean) => {
      if (uploaded) {
        super.ngOnInit();
      }
    });
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case "IMPORTED":
        return "Invite Pending";
      case "MESSAGE_SENT":
        return "Invited";
      case "FORM_SUBMITED":
        return "Data Submitted";
      case "PROCESSING":
        return "Processing";
      case "POLICY_ISSUED":
        return "Policy Issued";
      default:
        return status ?? "-";
    }
  }

  downloadReport(isXls: boolean) {
    this.isOpen = false;
    let pageSize = this.paginator?.pageSize ? this.paginator.pageSize : 10;
    const selectedIds = this.selection.selected.map((row) => row.id);
    const data = {
      skip: (this.paginator?.pageIndex ?? 0) * pageSize,
      take: pageSize,
      filter: this.search,
      orderBy: this.sort?.active,
      orderByDescending: this.sort?.direction === "desc",
      ids: selectedIds,
    };

    this._reportService.downloadAgencyHREmployeesReport(data, isXls);
  }
}
